import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Circle,
  Gavel,
  Loader2,
  Mail,
  MessageSquareQuote,
  Pencil,
  Sparkles,
  Trash2,
  Video,
} from 'lucide-react';
import type { PersonUpdate } from '../global';
import { useAsyncData } from '../features/directory/useAsyncData';
import {
  cardClass,
  formatDate,
  formatRelativeDate,
  initials,
  inputClass,
  pageTitle,
  primaryButton,
  secondaryButton,
  sectionTitle,
} from './ui/styles';
import { displaySummaryText } from '../features/recording/summaryRules';
import { plural, t } from '../lib/i18n';

interface PersonProfileViewProps {
  personId: string;
  isDarkMode: boolean;
  revision: number;
  onBack: () => void;
  onOpenMeeting: (meetingId: string) => void;
  onOpenCompany: (companyId: string) => void;
  onAskAI: (prompt: string) => void;
  /** Kişi düzenlendi / silindi — listeler tazelensin. */
  onChanged: () => void;
  onNotify: (kind: 'success' | 'error' | 'info', text: string) => void;
}

const NEW_COMPANY = '__new__';

/**
 * Kişi profili: o kişiyle yapılan tüm toplantılar, kişinin transkriptteki
 * kendi cümleleri, toplantılarda alınan kararlar ve kişiye atanmış aksiyonlar.
 */
export const PersonProfileView: React.FC<PersonProfileViewProps> = ({
  personId,
  isDarkMode,
  revision,
  onBack,
  onOpenMeeting,
  onOpenCompany,
  onAskAI,
  onChanged,
  onNotify,
}) => {
  const [localRevision, setLocalRevision] = useState(0);
  const profile = useAsyncData(() => window.zabit.getPersonProfile(personId), `${personId}:${revision}:${localRevision}`);
  const card = cardClass(isDarkMode);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '', role: '', companyId: '', newCompany: '', mergeWith: '' });
  const [isSaving, setIsSaving] = useState(false);
  const companies = useAsyncData(isEditing ? () => window.zabit.listCompanies() : null, isEditing);
  const people = useAsyncData(isEditing ? () => window.zabit.listPeople() : null, isEditing);

  const person = profile.data?.person;

  useEffect(() => {
    if (person && isEditing) {
      setDraft({
        name: person.name,
        email: person.email ?? '',
        role: person.role ?? '',
        companyId: person.companyId ?? '',
        newCompany: '',
        mergeWith: '',
      });
    }
  }, [person, isEditing]);

  const save = async () => {
    if (!person) return;
    setIsSaving(true);
    try {
      let companyId: string | null = draft.companyId || null;
      if (draft.companyId === NEW_COMPANY) {
        if (!draft.newCompany.trim()) throw new Error(t('Yeni şirketin adını yazın.', 'Enter a name for the new company.'));
        const domain = draft.email.includes('@') ? draft.email.split('@')[1] : undefined;
        companyId = (await window.zabit.createCompany(draft.newCompany.trim(), domain)).id;
      }
      const patch: PersonUpdate = {
        name: draft.name,
        email: draft.email.trim() || null,
        role: draft.role.trim() || null,
        companyId,
      };
      const result = await window.zabit.updatePerson(person.id, patch);
      // E-posta aynı olan kayıt zaten otomatik birleşti; elle seçildiyse tekrar birleştirme.
      if (draft.mergeWith && draft.mergeWith !== result.mergedWith?.id) {
        await window.zabit.mergePeople(person.id, draft.mergeWith);
      }
      setIsEditing(false);
      setLocalRevision((v) => v + 1);
      onChanged();
      onNotify(
        'success',
        result.mergedWith
          ? t(
              `Bu e-posta ${result.mergedWith.name} kaydında da vardı; iki kayıt birleştirildi.`,
              `This email also belonged to ${result.mergedWith.name}; the two records were merged.`
            )
          : t('Kişi güncellendi.', 'Person updated.')
      );
    } catch (err) {
      onNotify('error', (err as Error).message || t('Kişi güncellenemedi.', 'Could not update the person.'));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!person) return;
    const confirmed = window.confirm(
      t(
        `${person.name} kişilerden silinsin mi? Toplantılar silinmez, yalnızca kişi kaydı kalkar.`,
        `Remove ${person.name} from People? The meetings stay; only the person record is deleted.`
      )
    );
    if (!confirmed) return;
    try {
      await window.zabit.deletePerson(person.id);
      onChanged();
      onBack();
    } catch (err) {
      onNotify('error', (err as Error).message || t('Kişi silinemedi.', 'Could not delete the person.'));
    }
  };

  if (profile.isLoading && !profile.data) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-16 text-[13px] text-neutral-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('Profil yükleniyor…', 'Loading profile…')}
      </div>
    );
  }
  if (profile.error || !profile.data || !person) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center space-y-3">
        <p className="text-[13px] text-rose-400">{profile.error || t('Kişi bulunamadı.', 'Person not found.')}</p>
        <button onClick={onBack} data-native-back className="text-[13px] font-semibold text-[#46CBE2]">{t('Kişilere dön', 'Back to People')}</button>
      </div>
    );
  }

  const { meetings, decisions, excerpts, actionItems } = profile.data;
  const field = `w-full px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${inputClass(isDarkMode)}`;

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-7 select-none">
      <button onClick={onBack} data-native-back className="flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-neutral-300">
        <ArrowLeft className="w-3.5 h-3.5" /> {t('Kişiler', 'People')}
      </button>

      {/* Künye */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#1DA4BA] to-indigo-500 text-[#F0EEE6] font-bold text-[18px] flex items-center justify-center shrink-0">
            {initials(person.name)}
          </div>
          <div className="min-w-0">
            <h1 className={`${pageTitle(isDarkMode)} truncate`}>{person.name}</h1>
            <div className="flex items-center gap-3 flex-wrap text-[12.5px] text-neutral-500 mt-1">
              {person.role && <span>{person.role}</span>}
              {person.companyId && person.companyName && (
                <button onClick={() => onOpenCompany(person.companyId!)} className="flex items-center gap-1 hover:text-[#46CBE2]">
                  <Building2 className="w-3.5 h-3.5" /> {person.companyName}
                </button>
              )}
              {person.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> {person.email}
                </span>
              )}
            </div>
            <div className="text-[12px] text-neutral-600 mt-1">
              {t(`${person.meetingCount} toplantı`, plural(person.meetingCount, 'meeting', 'meetings'))} ·{' '}
              {t('son görüşme', 'last met')} {formatRelativeDate(person.lastMeetingISO)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setIsEditing((v) => !v)} className={secondaryButton(isDarkMode)}>
            <Pencil className="w-3.5 h-3.5" /> {t('Düzenle', 'Edit')}
          </button>
          <button
            onClick={() =>
              onAskAI(
                t(
                  `${person.name} ile yaptığım toplantıları, alınan kararları ve bekleyen işleri özetle`,
                  `Summarize my meetings with ${person.name}, the decisions made and the open items`
                )
              )
            }
            className={primaryButton}
          >
            <Sparkles className="w-3.5 h-3.5" /> {t('AI\'a sor', 'Ask AI')}
          </button>
        </div>
      </div>

      {isEditing && (
        <div className={`p-4 rounded-2xl border space-y-3 ${card}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('İsim', 'Name')}</span>
              <input className={field} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('E-posta', 'Email')}</span>
              <input
                className={field}
                type="email"
                value={draft.email}
                placeholder={t('ornek@sirket.com', 'name@company.com')}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
              {!person.email && (
                <span className="block text-[11px] text-neutral-500 leading-snug">
                  {t(
                    'Bu kişi takvim dışından katıldığı için e-postası bilinmiyor. E-posta girersen aynı e-postalı kayıtla otomatik birleşir ve sonraki takvim toplantıları bu profile bağlanır.',
                    'This person joined outside the calendar, so their email is unknown. Adding one merges them with any record that has the same email, and future calendar meetings link to this profile.'
                  )}
                </span>
              )}
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('Rol / unvan', 'Role / title')}</span>
              <input className={field} value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-neutral-500">{t('Şirket', 'Company')}</span>
              <select className={field} value={draft.companyId} onChange={(e) => setDraft({ ...draft, companyId: e.target.value })}>
                <option value="">{t('— Şirket yok —', '— No company —')}</option>
                {(companies.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
                <option value={NEW_COMPANY}>{t('+ Yeni şirket…', '+ New company…')}</option>
              </select>
            </label>
            {draft.companyId === NEW_COMPANY && (
              <label className="space-y-1 sm:col-span-2">
                <span className="text-[11px] font-semibold text-neutral-500">{t('Yeni şirketin adı', 'New company name')}</span>
                <input className={field} value={draft.newCompany} onChange={(e) => setDraft({ ...draft, newCompany: e.target.value })} />
              </label>
            )}
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[11px] font-semibold text-neutral-500">
                {t(
                  'Aynı kişinin başka bir kaydı varsa birleştir (o kaydın toplantıları buraya taşınır)',
                  'Merge another record of the same person (its meetings move here)'
                )}
              </span>
              <select className={field} value={draft.mergeWith} onChange={(e) => setDraft({ ...draft, mergeWith: e.target.value })}>
                <option value="">{t('— Birleştirme yok —', '— No merge —')}</option>
                {(people.data ?? [])
                  .filter((p) => p.id !== person.id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.email ? ` · ${p.email}` : ''} ({t(`${p.meetingCount} toplantı`, plural(p.meetingCount, 'meeting', 'meetings'))})
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => void remove()} className="flex items-center gap-1.5 text-[12px] font-semibold text-rose-400 hover:text-rose-300">
              <Trash2 className="w-3.5 h-3.5" /> {t('Kişiyi sil', 'Delete person')}
            </button>
            <div className="flex items-center gap-2">
              <button onClick={() => setIsEditing(false)} className="text-[12px] text-neutral-500 hover:text-neutral-300 px-2">
                {t('Vazgeç', 'Cancel')}
              </button>
              <button onClick={() => void save()} disabled={isSaving || !draft.name.trim()} className={primaryButton}>
                {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t('Kaydet', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplantılar */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <Video className="w-3.5 h-3.5 text-[#46CBE2]" /> {t('Birlikte yapılan toplantılar', 'Meetings together')} ({meetings.length})
        </h2>
        {meetings.length === 0 ? (
          <p className={`p-4 rounded-2xl border text-[13px] text-neutral-500 ${card}`}>{t('Henüz toplantı yok.', 'No meetings yet.')}</p>
        ) : (
          <div className={`rounded-2xl border divide-y ${card} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
            {meetings.map((m) => (
              <button key={m.id} onClick={() => onOpenMeeting(m.id)} className="w-full text-left p-4 hover:bg-white/[0.02] group">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[14px] font-semibold text-[#F0EEE6] truncate group-hover:text-[#46CBE2]">{m.title}</span>
                  <span className="text-[12px] text-neutral-500 shrink-0">{formatDate(m.recordedAtISO)}</span>
                </div>
                {m.tldr && <p className="text-[12.5px] text-neutral-400 mt-1 line-clamp-2">{displaySummaryText(m.tldr)}</p>}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Söyledikleri */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <MessageSquareQuote className="w-3.5 h-3.5 text-amber-400" /> {t('Transkriptten söyledikleri', 'What they said in transcripts')}
        </h2>
        {excerpts.length === 0 ? (
          <p className={`p-4 rounded-2xl border text-[12.5px] text-neutral-500 leading-relaxed ${card}`}>
            {t(
              'Bu kişinin konuşmacı olarak geçtiği bir transkript bulunamadı. Toplantının Transkript sekmesinde konuşmacı adına tıklayıp bu kişiyi seçerek eşleyebilirsin.',
              'No transcript lists this person as a speaker yet. Open a meeting’s Transcript tab, click a speaker name and match it to this person.'
            )}
          </p>
        ) : (
          <div className={`rounded-2xl border divide-y ${card} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
            {excerpts.map((x, i) => (
              <button key={`${x.meetingId}-${i}`} onClick={() => onOpenMeeting(x.meetingId)} className="w-full text-left p-3.5 hover:bg-white/[0.02]">
                <p className="text-[13px] text-neutral-300 leading-relaxed">“{x.text}”</p>
                <p className="text-[11.5px] text-neutral-500 mt-1">
                  {x.meetingTitle} · {formatDate(x.recordedAtISO)} · <span className="font-mono">{x.timestamp}</span>
                </p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Kararlar */}
      {decisions.length > 0 && (
        <section className="space-y-2.5">
          <h2 className={`${sectionTitle} flex items-center gap-2`}>
            <Gavel className="w-3.5 h-3.5 text-amber-400" /> {t('Alınan kararlar', 'Decisions made')}
          </h2>
          <div className={`p-4 rounded-2xl border space-y-2.5 ${card}`}>
            {decisions.map((d, i) => (
              <button key={i} onClick={() => onOpenMeeting(d.meetingId)} className="w-full text-left flex items-start gap-2.5 group">
                <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0 mt-2" />
                <span className="text-[13px] text-neutral-300 leading-relaxed">
                  {d.text} <span className="text-neutral-600 group-hover:text-[#46CBE2]">— {d.meetingTitle}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Aksiyonlar */}
      {actionItems.length > 0 && (
        <section className="space-y-2.5">
          <h2 className={sectionTitle}>{t('Kişiye atanan aksiyonlar', 'Action items assigned to them')}</h2>
          <div className={`p-4 rounded-2xl border space-y-2 ${card}`}>
            {actionItems.map((a) => (
              <button key={a.id} onClick={() => onOpenMeeting(a.meetingId)} className="w-full text-left flex items-start gap-2.5">
                {a.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="w-4 h-4 text-neutral-500 shrink-0 mt-0.5" />
                )}
                <span className={`text-[13px] leading-relaxed ${a.completed ? 'text-neutral-500 line-through' : 'text-neutral-300'}`}>
                  {a.text} <span className="text-neutral-600">— {a.meetingTitle}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
